"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, MapPin, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
export default function Hero() {
  const shouldReduceMotion = useReducedMotion();
  const heroImage = {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/budsys_hero_construction_bielsko.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/budsys_hero_construction_bielsko.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/budsys_hero_construction_bielsko.png-wvc-sizes",
    alt: "Nowoczesny budynek jednorodzinny realizowany przez firmę BUDSYS w rejonie Bielska-Białej"
  };
  const initialMotion = shouldReduceMotion ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 16
  };
  const animateMotion = {
    opacity: 1,
    y: 0
  };
  return <section id="hero" data-nav="dark" className="dark relative min-h-[90vh] w-full overflow-hidden bg-background text-foreground flex items-center">
      {/* Background documentary image */}
      <div className="absolute inset-0 z-0 select-none pointer-events-none">
        <img src={heroImage.src} data-wvc-srcset={heroImage.srcset} data-wvc-sizes={heroImage.sizes} alt={heroImage.alt} loading="eager" decoding="async" className="h-full w-full object-cover object-center scale-105" />
        {/* Cool-tone darkening architectural scrim */}
        <div className="absolute inset-0 bg-gradient-to-b from-background/90 via-background/65 to-background/95 backdrop-blur-[1px]" />
        {/* Subtle grid line overlay */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,transparent_0%,hsl(var(--background)/0.3)_100%)]" />
      </div>

      {/* Decorative architectural datum line & accent notch */}
      <div className="absolute top-0 left-0 right-0 z-10 h-px bg-gradient-to-r from-transparent via-border/50 to-transparent" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 py-24 sm:py-28 md:py-36 lg:px-12">
        <div className="max-w-3xl">
          {/* Territorial operational indicators */}
          <motion.div initial={initialMotion} animate={animateMotion} transition={{
          duration: 0.4,
          ease: [0.16, 1, 0.3, 1]
        }} className="flex flex-wrap items-center gap-3 mb-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3.5 py-1.5 backdrop-blur-md">
              <span className="size-2 rounded-full bg-accent animate-pulse" />
              <span className="text-xs font-semibold uppercase tracking-wider text-foreground">
                Bielsko-Biała + 25 km
              </span>
            </div>
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/40 px-3.5 py-1.5 backdrop-blur-md">
              <MapPin className="size-3.5 text-muted-foreground" />
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Posadzki: Cała Polska
              </span>
            </div>
          </motion.div>

          {/* Main H1 Title */}
          <motion.h1 initial={initialMotion} animate={animateMotion} transition={{
          duration: 0.4,
          delay: shouldReduceMotion ? 0 : 0.06,
          ease: [0.16, 1, 0.3, 1]
        }} className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-foreground leading-[1.08] mb-6">
            Solidna budowa domów i posadzki mikrocementowe
          </motion.h1>

          {/* Lead paragraph */}
          <motion.p initial={initialMotion} animate={animateMotion} transition={{
          duration: 0.4,
          delay: shouldReduceMotion ? 0 : 0.12,
          ease: [0.16, 1, 0.3, 1]
        }} className="text-lg sm:text-xl font-light text-muted-foreground leading-relaxed mb-8 max-w-2xl">
            Wznosimy domy jednorodzinne oraz prowadzimy prace ziemne w rejonie Bielska-Białej do 25 km. Posadzki mikrocementowe, żywiczne i dekoracyjne realizujemy na terenie całej Polski.
          </motion.p>

          {/* CTAs and guarantee indicator */}
          <motion.div initial={initialMotion} animate={animateMotion} transition={{
          duration: 0.4,
          delay: shouldReduceMotion ? 0 : 0.18,
          ease: [0.16, 1, 0.3, 1]
        }} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
            <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 border border-accent font-semibold px-8 py-6 h-auto shadow-md transition-[background-color,transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-lg">
              <Link to="https://budsys.pl/#kontakt" className="inline-flex items-center gap-2">
                <span>Zamów wycenę</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="border-border bg-card/40 hover:bg-card text-foreground font-medium px-6 py-6 h-auto backdrop-blur-sm">
              <Link to="/ParkietyiposadzkiPage">
                Zobacz posadzki i żywice
              </Link>
            </Button>
          </motion.div>

          {/* Trust badges footer */}
          <motion.div initial={initialMotion} animate={animateMotion} transition={{
          duration: 0.4,
          delay: shouldReduceMotion ? 0 : 0.24,
          ease: [0.16, 1, 0.3, 1]
        }} className="mt-12 pt-8 border-t border-border/50 flex flex-wrap items-center gap-6 sm:gap-10 text-xs sm:text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <ShieldCheck className="size-4 text-accent" />
              <span>Generalne wykonawstwo i nadzór</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="size-1.5 rounded-full bg-border" />
              <span>Bojszowy Nowe / Śląsk</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="size-1.5 rounded-full bg-border" />
              <span>Bezpośredni kontakt: +48 796 280 315</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}